import { useState, useEffect, useCallback, ChangeEvent, useMemo } from 'react';
import {
  Box,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Typography,
  Radio,
  RadioGroup,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { matchSorter } from 'match-sorter';
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import SearchInput from '../../inputs/SearchInput';
import StoreMappingRow from './StoreMappingRow';
import StoreMappingHeaderRow from './StoreMappingHeaderRow';
import NoStoresPlaceholder from '../../promotions/NoStoresPlaceholder';
import { StoreTargetMapping } from '../../../models/storetarget';
import { NewCampaignStore } from '../../../models/campaign';

interface Props {
  data: NewCampaignStore[];
  isEditMode: boolean;
}

interface PropertyItem {
  valueId?: string;
  value: string;
}

export default function StoreMappingGrid({ data, isEditMode }: Props) {
  const { control, watch, setValue } = useFormContext();

  const storeSearchTerm = watch('storeSearchTerm');

  const { field: selectedButtonField } = useController({
    name: 'selectedButton',
    control,
    defaultValue: 'unmapped',
  });

  const notApplicable = useWatch({
    name: 'notApplicable',
  });

  const handleButtonClick = (buttonName: string) => {
    selectedButtonField.onChange(buttonName);
  };

  const selectedButton = useWatch({
    control,
    name: 'selectedButton',
  });

  const propertyType = useWatch({
    control,
    name: 'propertyType',
  });

  const propertyName = useWatch({
    control,
    name: 'propertyName',
  });

  const propertyItems: PropertyItem[] = useWatch({
    control,
    name: 'propertyItems',
  });

  const { fields: StoreTargetMappingFields, replace } = useFieldArray({
    control,
    name: 'StoreTargetMappings',
  });

  const [selectedRadioValue, setSelectedRadioValue] = useState(
    propertyType === 'multi-select' ? propertyItems[0]?.value : 'Yes',
  );

  const StoreTargetMappingsWithoutIds = StoreTargetMappingFields.map(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ id, ...rest }) => rest as StoreTargetMapping,
  );

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkIfCanScroll = () => {
    const container = document.querySelector('.toggle-button-scroll-container');
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth,
      );
    }
  };

  useEffect(() => {
    const container = document.querySelector('.toggle-button-scroll-container');
    if (container) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      checkIfCanScroll();
      container.addEventListener('scroll', checkIfCanScroll);
      return () => container.removeEventListener('scroll', checkIfCanScroll);
    }
  }, []);

  useEffect(() => {
    if (StoreTargetMappingFields.length === 0) {
      if (!isEditMode) {
        // Create mode: Initialize mappings for the first time
        const initialValues = data.map((target) => ({
          storesId: target.id,
          bannerName: target.banner.name,
          store: target.name,
          location: `${target.address?.city}, ${target.address?.state}`,
          propertyValueId: '', // Start with empty values in create mode
          propertyValue: '',
        }));

        replace(initialValues); // Initialize store mappings in create mode
      } else {
        // Edit mode: Populate the mappings based on the backend response

        const initialValues = data.map((store) => {
          // Ensure we are using the updated propertyItems, not just the backend properties

          const matchingProperty = propertyItems.find((item) =>
            store.properties?.some(
              (property) => item.valueId === property?.value_id.toString(),
            ),
          );

          const isNotApplicable = store.properties?.some(
            (property) =>
              property?.value_id.toString() === notApplicable?.valueId,
          );

          return {
            storesId: store.id,
            bannerName: store.banner.name,
            store: store.name,
            location: `${store.address.city}, ${store.address.state}`,
            propertyValueId:
              matchingProperty?.valueId ||
              (isNotApplicable ? notApplicable?.valueId : ''),
            propertyValue:
              matchingProperty?.value ||
              (isNotApplicable ? notApplicable?.value : ''),
          };
        });

        replace(initialValues);
      }
    } else {
      const validPropertyIds = propertyItems.map((item) => item.valueId);

      setValue(
        'StoreTargetMappings',
        StoreTargetMappingsWithoutIds.map((mapping) => {
          if (mapping.propertyValue === 'Not Applicable') {
            return mapping; // Keep "Not Applicable" unchanged
          }

          const correspondingItem = propertyItems.find(
            (item) => item.valueId === mapping.propertyValueId,
          );

          // If the propertyValueId is invalid, reset it
          if (!validPropertyIds.includes(mapping.propertyValueId)) {
            return { ...mapping, propertyValue: '', propertyValueId: '' };
          }

          // If the propertyValueId is valid but the propertyValue is incorrect, update it
          if (
            correspondingItem &&
            correspondingItem.value !== mapping.propertyValue
          ) {
            return { ...mapping, propertyValue: correspondingItem.value };
          }

          return mapping;
        }),
      );
    }
  }, [isEditMode]);

  const filteredTargets = useMemo(() => {
    let targetsToFilter = StoreTargetMappingsWithoutIds;

    if (selectedButton === 'unmapped') {
      targetsToFilter = targetsToFilter.filter(
        (mapping) => !mapping.propertyValue || mapping.propertyValue === '',
      );
    } else if (selectedButton === 'notApplicable') {
      targetsToFilter = targetsToFilter.filter(
        (mapping) => mapping.propertyValue === 'Not Applicable',
      );
    } else {
      targetsToFilter = targetsToFilter.filter(
        (mapping) => mapping.propertyValue === selectedButton,
      );
    }

    if (storeSearchTerm && storeSearchTerm !== '') {
      return matchSorter<StoreTargetMapping>(targetsToFilter, storeSearchTerm, {
        keys: ['store', 'bannerName', 'location'],
      });
    }
    return targetsToFilter;
  }, [storeSearchTerm, StoreTargetMappingsWithoutIds, selectedButton]);

  const { field: searchField } = useController({
    name: 'storeSearchTerm',
    control,
  });

  const { field: activeStoresField } = useController({
    name: 'activeStores',
    control,
  });

  const activeStores: number[] = watch('activeStores', []);

  const selectedTargetCount = useMemo(
    () => activeStores?.length,
    [activeStores],
  );

  const isAllTargetsSelected: boolean = useMemo(() => {
    if (activeStores.length < 1) {
      return false;
    }

    return filteredTargets.every(
      (store) =>
        activeStores.findIndex(
          (activeStore) => activeStore === store.storesId,
        ) !== -1,
    );
  }, [activeStores, filteredTargets]);

  const onSelectAllTargets = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (checked) {
        const allTargetIds = filteredTargets.map((target) => target.storesId);
        activeStoresField.onChange(allTargetIds);
      } else {
        activeStoresField.onChange([]);
      }
    },
    [activeStoresField, filteredTargets],
  );

  const unmappedCount = useMemo(() => {
    return StoreTargetMappingsWithoutIds.filter(
      (target) => target.propertyValue === '',
    ).length;
  }, [StoreTargetMappingsWithoutIds]);

  const propertyItemCounts = useMemo(() => {
    return propertyItems?.reduce(
      (acc, item) => {
        acc[item.value] = StoreTargetMappingsWithoutIds.filter(
          (target) => target.propertyValue === item.value,
        ).length;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [propertyItems, StoreTargetMappingsWithoutIds]);

  const notApplicableCount = useMemo(() => {
    return StoreTargetMappingsWithoutIds.filter(
      (target) => target.propertyValue === 'Not Applicable',
    ).length;
  }, [StoreTargetMappingsWithoutIds]);

  const columns =
    selectedButton === 'unmapped'
      ? ['Store', 'Banner', 'Location']
      : ['Store', 'Banner', 'Location', propertyName];

  const handleSaveMapping = () => {
    const StoreTargetMappingsWithoutIds = StoreTargetMappingFields.map(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ id, ...rest }) => rest as StoreTargetMapping,
    );

    const selectedPropertyItem = propertyItems.find(
      (item) => item.value === selectedRadioValue,
    );

    // Step 3: Map over the existing mappings and update `propertyValue` and `propertyValueId`
    const updatedMappings = StoreTargetMappingsWithoutIds.map((mapping) => {
      if (activeStores.includes(mapping.storesId)) {
        return {
          ...mapping,
          propertyValue: selectedRadioValue,
          propertyValueId: selectedPropertyItem?.valueId || null, // Assign `valueId` or `null`
        };
      }
      return mapping;
    });

    replace(updatedMappings);

    activeStoresField.onChange([]);
  };

  const handleScroll = (direction: 'left' | 'right') => {
    const container = document.querySelector('.toggle-button-scroll-container');
    if (container) {
      const scrollAmount = 200; // Adjust this value as needed
      container.scrollLeft +=
        direction === 'left' ? -scrollAmount : scrollAmount;
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'hidden',
        gap: '24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'secondary.light',
          position: 'relative',
          alignItems: 'center',
        }}
      >
        <Box
          className="toggle-button-scroll-container"
          sx={{
            display: 'flex',
            overflowX: 'auto',
            position: 'relative',
            maxWidth: 'calc(100% - 80px)', // Make room for arrows
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <ToggleButtonGroup
            value={selectedButton}
            exclusive
            onChange={(_, newButton) => {
              if (newButton !== null) {
                handleButtonClick(newButton);
                activeStoresField.onChange([]);
              }
            }}
            sx={{
              display: 'flex',
              height: '50px',
              '& .MuiTypography-root': {
                textTransform: 'capitalize',
              },
              '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                borderRadius: '0',
                marginLeft: '0 !important',
                borderLeft: 'none !important',
                width: 'max-content',
                height: '50px',
                minWidth: '120px',
              },
              '& .MuiToggleButtonGroup-grouped.Mui-selected': {
                backgroundColor: 'transparent',
                borderBottom: '2px solid',
                borderColor: 'primary.light',
                '& .MuiTypography-root': {
                  fontWeight: 600,
                  color: 'fileInput.dark',
                },
              },
            }}
          >
            <ToggleButton value="unmapped">
              <Typography variant="body2">{`${unmappedCount} Unmapped`}</Typography>
            </ToggleButton>

            {propertyItems?.map((item, index) => (
              <ToggleButton key={index} value={item.value}>
                <Typography variant="body2">{`${
                  propertyItemCounts[item.value] || 0
                } ${item.value}`}</Typography>
              </ToggleButton>
            ))}

            {propertyType === 'multi-select' && (
              <ToggleButton value="notApplicable">
                <Typography variant="body2">{`${notApplicableCount} Not Applicable`}</Typography>
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>

        {(canScrollLeft || canScrollRight) && (
          <div className="absolute right-0 flex gap-1">
            <Button
              disabled={!canScrollLeft}
              onClick={() => handleScroll('left')}
              size="icon"
              variant="outline"
            >
              <ChevronLeft />
            </Button>
            <Button
              disabled={!canScrollRight}
              onClick={() => handleScroll('right')}
              size="icon"
              variant="outline"
            >
              <ChevronRight />
            </Button>
          </div>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          overflowY: 'hidden',
          gap: '24px',
          paddingTop: '4px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '70%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {selectedTargetCount}
              </Box>
              {` store${selectedTargetCount > 1 ? 's' : ''} selected`}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                width: '30%',
                minWidth: '240px',
              }}
            >
              <SearchInput
                searchInput={searchField.value}
                onChange={(e) => {
                  searchField.onChange(e);
                }}
                onClear={() => searchField.onChange('')}
                wrapperClassName="w-full"
              />
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              border: '1px solid',
              borderColor: 'secondary.light',
              borderRadius: '8px',
              overflowY: 'auto',
              height: 'fit-content',
            }}
          >
            <Table
              stickyHeader
              sx={{
                '& .MuiTableCell-root': {
                  padding: '0 8px',
                  width: '25%',
                  height: '36px',
                },
                '& .MuiTableCell-head:first-of-type': {
                  paddingLeft: '16px',
                },
              }}
            >
              <TableHead
                sx={{
                  backgroundColor: 'secondary.main',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                }}
              >
                <StoreMappingHeaderRow
                  filteredTargets={filteredTargets}
                  columns={columns}
                  isAllTargetsSelected={isAllTargetsSelected}
                  onSelectAllTargets={onSelectAllTargets}
                />
              </TableHead>

              <TableBody
                sx={{
                  '& .MuiTableRow-root:last-of-type': {
                    '& .MuiTableCell-root': {
                      borderBottom: 'none',
                    },
                  },
                }}
              >
                {filteredTargets.length === 0 ? (
                  selectedButton === 'unmapped' ? (
                    <TableRow
                      sx={{
                        height: '315px',
                        backgroundColor: 'secondary.main',
                      }}
                    >
                      <TableCell
                        colSpan={columns.length}
                        sx={{
                          border: 'none',
                          height: '100%',
                          textAlign: 'center',
                        }}
                      >
                        <Box
                          component="img"
                          src="/allStoresAssignedStatus.png"
                          alt="All stores assigned"
                          sx={{
                            margin: '0 auto',
                            height: 120,
                            width: 120,
                          }}
                        />
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: 'secondary.dark',
                            paddingTop: '16px',
                          }}
                        >
                          All stores have been assigned.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow
                      sx={{
                        height: '315px',
                        backgroundColor: 'secondary.main',
                      }}
                    >
                      <TableCell
                        colSpan={columns.length}
                        sx={{
                          border: 'none',
                          height: '100%',
                          textAlign: 'center',
                        }}
                      >
                        <NoStoresPlaceholder text="No stores are assigned to this group." />
                      </TableCell>
                    </TableRow>
                  )
                ) : (
                  filteredTargets.map((target) => (
                    <StoreMappingRow
                      key={target.storesId}
                      target={target}
                      selected={activeStores.includes(target.storesId)}
                      onChange={() => {
                        const index = activeStores.findIndex(
                          (store: number) => store === target.storesId,
                        );
                        if (index === -1) {
                          activeStoresField.onChange([
                            ...activeStores,
                            target.storesId,
                          ]);
                        } else {
                          activeStoresField.onChange(
                            activeStores.filter(
                              (store) => store !== target.storesId,
                            ),
                          );
                        }
                      }}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Total stores: {filteredTargets.length}
          </Typography>
        </Box>

        <Box
          sx={{
            width: '30%',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'secondary.light',
            backgroundColor: 'secondary.main',
            borderRadius: '8px',
          }}
        >
          <Box sx={{ padding: '0 12px', height: '36px' }}>
            <Typography
              variant="body2"
              sx={{ lineHeight: '36px', color: 'fileInput.dark' }}
            >
              {propertyName}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              borderTop: '1px solid',
              borderColor: 'secondary.light',
            }}
          >
            <Box>
              <RadioGroup
                key={propertyType}
                value={selectedRadioValue}
                onChange={(e) => setSelectedRadioValue(e.target.value)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  '& .MuiRadio-root': {
                    color: 'secondary.dark',
                    height: '32px',
                  },
                  '& .Mui-checked': {
                    color: 'primary.dark',
                  },
                  '& .MuiTypography-root': {
                    fontSize: '14px',
                  },
                }}
              >
                {propertyType === 'boolean' ? (
                  propertyItems?.map((item, index) => (
                    <FormControlLabel
                      key={index}
                      value={item.value}
                      control={<Radio size="small" />}
                      label={item.value}
                      sx={{
                        backgroundColor:
                          selectedRadioValue === item.value
                            ? 'selectedRow.main'
                            : 'transparent',
                        margin: 0,
                        paddingRight: '12px',
                      }}
                    />
                  ))
                ) : (
                  <>
                    {propertyItems?.map((item, index) => (
                      <FormControlLabel
                        key={index}
                        value={item.value}
                        control={<Radio size="small" />}
                        label={item.value}
                        sx={{
                          backgroundColor:
                            selectedRadioValue === item.value
                              ? 'selectedRow.main'
                              : 'transparent',
                          margin: 0,
                          paddingRight: '12px',
                        }}
                      />
                    ))}

                    <FormControlLabel
                      value="Not Applicable"
                      control={<Radio size="small" />}
                      label="Not Applicable"
                      sx={{
                        backgroundColor:
                          selectedRadioValue === 'Not Applicable'
                            ? 'selectedRow.main'
                            : 'transparent',
                        margin: 0,
                        paddingRight: '12px',
                      }}
                    />
                  </>
                )}
              </RadioGroup>
            </Box>
            <div className="p-3">
              <Button
                className="w-full"
                disabled={
                  filteredTargets.length === 0 || selectedTargetCount === 0
                }
                onClick={handleSaveMapping}
              >
                Save
              </Button>
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
